import React from 'react';
import { useOrganizationClientRecords } from '../../../../../../../hooks/api/useOrganizationClientRecords';
import { APITypes } from '../../../../../../../primary-record-types';
import Stack, { ContentStack } from '../../../../../../v2BaseComponents/Stack';
import { AnimateLoadedContent } from '../../../../../../v2BaseComponents/Loader';
import { getIntl } from '../../../../../../../utils/intl';
import InfoIcon from '../../../../../../v2BaseComponents/InfoIcon';
import { PromptActions } from '../../../../../../v2BaseComponents/Prompt';
import getName from '../../../../../../../utils/getName';
import { clientRecordStatusTypeGuard } from '../../../../../../../utils/clientRecordsUtil';

const intl = getIntl('revoke-invitation-prompt-content', {
    revokeInvitation: 'Revoke Invitation',
    revokeInvitationPrompt: 'Are you sure you want to revoke the invitation for {clientName}?',
    revokeProvisionedProfileInvitationPrompt:
        "Are you sure you want to revoke the invitation for {clientName}?  This will delete their profile.  If there is an issue with the invitation, it is advised to edit the invitation instead of revoking the invitation.  This will not delete the client's profile.",
    revoke: 'Revoke',
    cancel: 'Cancel',
    editInvitation: 'Edit Invitation',
});

export type RevokeInvitationPromptContentOwnProps = {
    onClose: () => void;
    organizationClientRecordId: string | undefined;
    onRevoke: (organizationClientInvitationId: string) => void;
    onEditInvitation: () => void;
};

export type RevokeInvitationPromptContentProps = RevokeInvitationPromptContentOwnProps;

const Content: React.FC<{
    invitation: APITypes.organizationService.GetOrganizationClientInvitationForOrganizationClientRecordResponse;
    onRevoke: () => void;
    onClose: () => void;
    clientRecord: APITypes.organizationService.GetOrganizationClientRecordResponse;
    onEditInvitation: () => void;
}> = ({ invitation, onRevoke, onClose, clientRecord, onEditInvitation }) => {
    const isProvisionedProfile =
        clientRecordStatusTypeGuard.invited(clientRecord) && !!clientRecord.provisionedMedicalProfileId;
    return (
        <ContentStack>
            <InfoIcon
                alertIconProps={{
                    name: 'AlertTriangle',
                    variant: 'warning',
                }}
                title={intl('revokeInvitation')}
                subText={intl(
                    isProvisionedProfile ? 'revokeProvisionedProfileInvitationPrompt' : 'revokeInvitationPrompt',
                    {
                        clientName: getName({
                            firstName: invitation.clientFirstName,
                            lastName: invitation.clientLastName,
                        }),
                    },
                )}
            />
            <PromptActions
                actions={[
                    {
                        title: intl('editInvitation'),
                        onPress: onEditInvitation,
                        variant: 'primary',
                        icon: 'Edit',
                        testID: 'confirm-edit-invitation',
                        show: isProvisionedProfile,
                    },
                    {
                        title: intl('revoke'),
                        onPress: onRevoke,
                        icon: 'Trash2',
                        variant: 'alert',
                        testID: 'confirm-revoke-invitation',
                    },
                    {
                        title: intl('cancel'),
                        onPress: onClose,
                        variant: 'secondary',
                        icon: 'XCircle',
                        testID: 'cancel-revoke-invitation',
                    },
                ]}
            />
        </ContentStack>
    );
};

const RevokeInvitationPromptContent: React.FC<RevokeInvitationPromptContentProps> = ({
    onClose,
    organizationClientRecordId,
    onRevoke,
    onEditInvitation,
}) => {
    const { isLoadingClientRecordInvitation, clientRecordInvitation, clientRecord, isLoadingClientRecord } =
        useOrganizationClientRecords({
            organizationClientRecordId,
            loadClientRecordInvitation: true,
        });

    const isLoading =
        isLoadingClientRecordInvitation || !clientRecordInvitation || !clientRecord || isLoadingClientRecord;

    return (
        <Stack vertical fullWidth style={{ minHeight: 200 }}>
            <AnimateLoadedContent isLoading={isLoading}>
                {() =>
                    clientRecordInvitation ? (
                        <Content
                            invitation={clientRecordInvitation}
                            onRevoke={() => {
                                onRevoke(clientRecordInvitation.organizationClientInvitationId);
                            }}
                            onClose={onClose}
                            clientRecord={clientRecord!}
                            onEditInvitation={onEditInvitation}
                        />
                    ) : null
                }
            </AnimateLoadedContent>
        </Stack>
    );
};

export default React.memo(RevokeInvitationPromptContent);
